/**
 * SCROLL-DRIVEN ZOOM (2026-08-29).
 *
 * Every picture marked `.d2-zoom__inner` opens 20% oversized and settles back
 * to its true size as it travels up the screen. The progress is bound to the
 * scroll POSITION, not to a clock: scroll back up and it plays back. That is
 * why none of this can be done with the site's --d2-dur/--d2-ease pair, which
 * describe state switches and own a duration (see the token comment in
 * assets/duet-tokens.css).
 *
 * Reverse-engineered from the reference's own theme, which drives it with
 * GSAP ScrollTrigger (`scrub`, `fromTo({scale:1.2},{scale:1})`, default
 * start/end). Twelve sampled points matched
 *
 *     scale(t) = 1 + 0.2 * (1 - t)^2            [GSAP's power1.out]
 *     t = (viewportHeight - rect.top) / (rect.height + viewportHeight)
 *
 * to four decimal places on both the phone and the desktop, so the formula
 * below is a copy rather than an approximation. Full evidence:
 * docs/调研/Rhode全站拆解_2026-08-18/动效补遗_轮播缩放_2026-08-29.md
 *
 * IntersectionObserver + rAF rather than GSAP (70 KB for one scale) and rather
 * than `animation-timeline: view()` (same interval for free, but the older
 * Safaris in the traffic would silently show nothing). The observer is what
 * keeps the cost flat: only pictures actually on screen are measured, so a
 * page with thirty of them still reads a handful per frame.
 *
 * The reference smooths with `scrub: 0.1`. Not copied — a 0.1s catch-up on a
 * 20% move is close to invisible and it would mean a permanently resident rAF
 * loop instead of one that stops the moment scrolling does.
 *
 * WHAT GETS THE CLASS is a rule, not a taste: pictures somebody uses to make a
 * buying decision never move. Product packshots, shade cards, the PDP gallery
 * — all excluded, because you cannot judge a colour or a size against
 * something that is still resizing. The reference excludes exactly the same
 * set (60/60 product cards, 16/16 PDP gallery frames) and keeps it on
 * atmosphere and editorial pictures. Ours: the home UGC rail, the two reads
 * cards, the three mechanism cards, the hero, and (2026-09-28) the couple in
 * the PDP numbers band.
 */
(() => {
  const SELECTOR = '.d2-zoom__inner';

  // Reduced motion switches the whole thing off rather than shortening it.
  // The global reduce block in duet-tokens.css only flattens animation and
  // transition DURATIONS; an inline transform written by script has neither,
  // so it would sail straight through and keep moving. This is the only guard
  // that actually holds.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const targets = Array.from(document.querySelectorAll(SELECTOR));
  if (!targets.length) return;

  // The two ends live in the stylesheet so the look is edited where every
  // other visual constant is. Read once — they are design tokens, not state.
  const rootStyle = getComputedStyle(document.documentElement);
  const readToken = (name, fallback) => {
    const value = parseFloat(rootStyle.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };
  const FROM = readToken('--d2-zoom-from', 1.2);
  const TO = readToken('--d2-zoom-to', 1);
  const SPAN = FROM - TO;

  // Only what is on screen right now. rootMargin gives a little lead-in so a
  // picture is already carrying the right scale on the frame it appears.
  const live = new Set();
  let ticking = false;

  const paint = () => {
    ticking = false;
    const viewport = window.innerHeight || document.documentElement.clientHeight;

    live.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const travel = rect.height + viewport;
      if (travel <= 0) return;

      // 0 when the top edge is level with the bottom of the screen, 1 when the
      // bottom edge is level with the top of it. Clamped, because the observer
      // fires a little outside both ends.
      const t = Math.min(1, Math.max(0, (viewport - rect.top) / travel));
      const rest = 1 - t;
      el.style.transform = 'scale(' + (TO + SPAN * rest * rest).toFixed(4) + ')';
    });
  };

  const tick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(paint);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          live.add(entry.target);
        } else {
          live.delete(entry.target);
          // Park it at the far end rather than leaving the last on-screen value
          // frozen mid-move: a picture that has left upwards is finished, and
          // one still below has not started.
          entry.target.style.transform =
            'scale(' + (entry.boundingClientRect.top > 0 ? FROM : TO).toFixed(4) + ')';
        }
      });
      tick();
    },
    { rootMargin: '10% 0px' }
  );

  targets.forEach((el) => observer.observe(el));

  // Horizon hands scrolling to .page-wrapper above 990 (base.css: 100dvh,
  // overflow-y auto) and pins html/body at overflow:hidden, so the window
  // never fires a scroll event on the desktop. Listening on the document in
  // the CAPTURE phase catches both: scroll does not bubble, but it does
  // capture down to whichever element is actually scrolling.
  document.addEventListener('scroll', tick, { passive: true, capture: true });
  window.addEventListener('resize', tick, { passive: true });
  window.addEventListener('load', tick);
  tick();
})();
